import { message } from 'antd'
import axios from 'axios'
import { get } from 'lodash'
import { useAuthStore } from './auth'

const request = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/api`,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
request.interceptors.request.use((config) => {
  if (!config.headers['Skip-Auth']) {
    const { accessToken, selectedTenant } = useAuthStore.getState()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    if (selectedTenant) {
      config.headers['X-Tenant-ID'] = selectedTenant.id
    }

    // For workspace-specific endpoints, ensure tenant is selected
    if (config.url?.includes('/workspaces/') && !selectedTenant) {
      return Promise.reject(new Error('Tenant selection required - please sign in again'))
    }
  } else {
    delete config.headers['Skip-Auth']
  }

  // Note: Workspace ID is added to API calls via query params in individual API functions
  // to avoid circular dependency issues with the workspace store

  // Don't set Content-Type for FormData - let browser set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }

  return config
})

request.interceptors.response.use(
  (res: any) => res,
  (error: any) => {
    if (error?.config?.raw) throw error

    if (axios.isCancel(error)) {
      return Promise.reject('Request cancelled')
    }

    let errMsg: string
    let shouldLogout = false

    // Handle specific error types
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      errMsg = 'Request timeout - please try again or contact support if the issue persists'
    } else if (error.code === 'ERR_NETWORK') {
      errMsg = 'Network error - please check your connection and try again'
    } else if (error.response?.status === 401) {
      errMsg = 'Authentication failed - please sign in again'
      shouldLogout = true
    } else if (error.response?.status === 403) {
      errMsg = "Access denied - you don't have permission for this action"
    } else if (error.response?.status === 404) {
      errMsg = 'Resource not found - the requested item may have been deleted'
    } else if (error.response?.status === 500) {
      errMsg = 'Server error - please try again later or contact support'
    } else if (error.response?.status === 400) {
      // Check if this is a tenant_id undefined error
      const serverMsg =
        get(error, 'response.data.error.message') || get(error, 'response.data.message')
      if (serverMsg && serverMsg.includes('tenant_id') && serverMsg.includes('undefined')) {
        errMsg = 'Multi-tenant authentication required - please sign out and sign back in'
        shouldLogout = true
      } else {
        errMsg = serverMsg || 'Bad request - please check your input and try again'
      }
    } else if (error.response?.status >= 400 && error.response?.status < 500) {
      // Client errors - use server message if available
      const serverMsg =
        get(error, 'response.data.error.message') || get(error, 'response.data.message')
      errMsg =
        serverMsg ||
        `Request failed (${error.response.status}) - please check your input and try again`
    } else if (error.response?.status >= 500) {
      errMsg = 'Server error - please try again later or contact support'
    } else {
      // Fallback for other errors
      const err =
        get(error, 'response.data.error.message') ||
        get(error, 'response.data.message') ||
        error.message ||
        'An unexpected error occurred - please try again'

      errMsg = typeof err === 'string' ? err : err?.[0]
    }

    // Handle logout if needed
    if (shouldLogout) {
      const { clearAuth } = useAuthStore.getState()
      clearAuth()
      // Redirect to sign in page using React Router
      if (typeof window !== 'undefined') {
        window.location.href = '/login'
      }
    }

    const showMessage = !error?.config?.hideMessage

    if (showMessage) {
      message.error(errMsg)
    }

    return Promise.reject(new Error(errMsg))
  }
)

export default request
